import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "../firebase";

// ── Estados posibles de la página ───────────────────────────────────────────
const STATES = {
  LOADING:  "loading",
  FORM:     "form",
  SUCCESS:  "success",
  EXPIRED:  "expired",
  INVALID:  "invalid",
};

export default function AuthAction() {
  const [searchParams]   = useSearchParams();
  const navigate         = useNavigate();

  const mode    = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [pageState, setPageState]     = useState(STATES.LOADING);
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirm, setConfirm]         = useState("");
  const [showPass, setShowPass]       = useState(false);
  const [error, setError]             = useState("");
  const [loading, setLoading]         = useState(false);

  // ── Verificar el código al montar ─────────────────────────────────────────
  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setPageState(STATES.INVALID);
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setPageState(STATES.FORM);
      })
      .catch((err) => {
        if (
          err.code === "auth/expired-action-code" ||
          err.code === "auth/invalid-action-code"
        ) {
          setPageState(STATES.EXPIRED);
        } else {
          setPageState(STATES.INVALID);
        }
      });
  }, [mode, oobCode]);

  // ── Confirmar nueva contraseña ────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPageState(STATES.SUCCESS);
    } catch (err) {
      if (err.code === "auth/expired-action-code") {
        setPageState(STATES.EXPIRED);
      } else if (err.code === "auth/weak-password") {
        setError("La contraseña es demasiado débil. Usa al menos 6 caracteres.");
      } else {
        setError("Ocurrió un error. Solicita un nuevo enlace de recuperación.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#0f2027] via-[#203a43] to-[#2c5364] px-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-extrabold tracking-widest text-yellow-400 drop-shadow-xl">
            SIT
          </h1>
          <p className="text-green-200 text-sm italic mt-1">
            Social Interactive Technology
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 p-8">

          {/* ── LOADING ── */}
          {pageState === STATES.LOADING && (
            <div className="flex flex-col items-center gap-4 py-6">
              <svg className="animate-spin h-10 w-10 text-yellow-400" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-white/70 text-sm">Verificando enlace...</p>
            </div>
          )}

          {/* ── FORMULARIO ── */}
          {pageState === STATES.FORM && (
            <>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Nueva contraseña
              </h2>
              <p className="text-white/60 text-sm text-center mb-6">
                Cuenta: <span className="text-yellow-300 font-medium">{email}</span>
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nueva contraseña */}
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Nueva contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-white/20 placeholder-white/60 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition"
                    tabIndex={-1}
                  >
                    {showPass ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Confirmar contraseña */}
                <div>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Confirmar contraseña"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className={`w-full px-4 py-3 rounded-xl bg-white/20 placeholder-white/60 text-white focus:outline-none focus:ring-2 transition ${
                      confirm && confirm !== password
                        ? "ring-2 ring-red-400 focus:ring-red-400"
                        : "focus:ring-green-400"
                    }`}
                  />
                  {confirm && confirm !== password && (
                    <p className="text-red-300 text-xs mt-1 ml-1">
                      Las contraseñas no coinciden
                    </p>
                  )}
                </div>

                {/* Indicador de fuerza */}
                {password && (
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          password.length >= (i + 1) * 3
                            ? password.length >= 12 ? "bg-green-400"
                              : password.length >= 8  ? "bg-yellow-400"
                              : "bg-red-400"
                            : "bg-white/20"
                        }`}
                      />
                    ))}
                  </div>
                )}

                {error && (
                  <p className="text-red-300 text-sm bg-red-500/20 rounded-lg px-3 py-2 text-center">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || !password || password !== confirm}
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 via-green-400 to-teal-500 text-white font-bold rounded-xl shadow-lg hover:from-yellow-500 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Guardando...
                    </>
                  ) : (
                    "Cambiar contraseña"
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── ÉXITO ── */}
          {pageState === STATES.SUCCESS && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                ¡Contraseña actualizada!
              </h2>
              <p className="text-white/60 text-sm mb-6">
                Tu contraseña ha sido cambiada exitosamente. Ya puedes iniciar sesión.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-teal-500 text-white font-bold rounded-xl hover:from-yellow-500 hover:to-teal-600 transition"
              >
                Ir al inicio de sesión
              </button>
            </div>
          )}

          {/* ── ENLACE EXPIRADO ── */}
          {pageState === STATES.EXPIRED && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Enlace expirado
              </h2>
              <p className="text-white/60 text-sm mb-6">
                Este enlace ya no es válido. Los enlaces de recuperación expiran en 1 hora.
                Solicita uno nuevo.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-teal-500 text-white font-bold rounded-xl hover:from-yellow-500 hover:to-teal-600 transition"
              >
                Solicitar nuevo enlace
              </button>
            </div>
          )}

          {/* ── ENLACE INVÁLIDO ── */}
          {pageState === STATES.INVALID && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Enlace inválido
              </h2>
              <p className="text-white/60 text-sm mb-6">
                Este enlace no es válido o ya fue utilizado.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-teal-500 text-white font-bold rounded-xl hover:from-yellow-500 hover:to-teal-600 transition"
              >
                Volver al inicio
              </button>
            </div>
          )}

        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          © {new Date().getFullYear()} SIT — Social Interactive Technology
        </p>
      </div>
    </div>
  );
}
